import styled, { css } from "styled-components";
import { theme } from "styles/theme";
import { zIndex } from "styles";

const PILL_HEIGHT = 60;
const BLUE = "#1E5AF6";

export const LauncherWrapper = styled.div`
  position: fixed;
  right: 32px;
  bottom: 32px;
  z-index: ${zIndex.floatingButton};
`;

export const LauncherButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  height: ${PILL_HEIGHT}px;
  min-width: ${PILL_HEIGHT}px;
  padding: 0;
  border: none;
  border-radius: ${PILL_HEIGHT / 2}px;
  background: ${BLUE};
  color: #ffffff;
  font-family: ${theme.fonts.primary};
  text-align: left;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(30, 90, 246, 0.35);
  transition: transform 150ms ease;

  &:hover {
    transform: scale(1.03);
  }

  &[aria-busy="true"] {
    opacity: 0.8;
    cursor: progress;
  }
`;

export const CocoCircle = styled.span`
  position: relative;
  flex: none;
  width: ${PILL_HEIGHT}px;
  height: ${PILL_HEIGHT}px;
  border-radius: 50%;
  border: 3px solid ${BLUE};
  background: #ffffff;
  box-sizing: border-box;
  overflow: visible;

  img {
    border-radius: 50%;
  }
`;

export const StatusDot = styled.span<{ $isOnline: boolean }>`
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 18px;
  height: 18px;
  border: 3px solid ${BLUE};
  border-radius: 50%;
  background: ${({ $isOnline }) => ($isOnline ? "#4CAF50" : "#9E9EA6")};
  box-sizing: border-box;
`;

export const UnreadBadge = styled.span`
  position: absolute;
  top: -6px;
  left: -6px;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border: 2px solid #ffffff;
  border-radius: 12px;
  background: #b5322e;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  box-sizing: border-box;
`;

export const LabelWrapper = styled.span<{ $isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  white-space: nowrap;
  transition:
    max-width 250ms ease,
    opacity 200ms ease,
    padding 250ms ease;

  ${({ $isCollapsed }) =>
    $isCollapsed
      ? css`
          max-width: 0;
          padding: 0;
          opacity: 0;
        `
      : css`
          max-width: 240px;
          padding: 0 24px 0 4px;
          opacity: 1;
        `}
`;

export const LabelTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
`;

export const LabelStatus = styled.span<{ $isUnread: boolean }>`
  font-size: 14px;
  line-height: 18px;
  font-weight: ${({ $isUnread }) => ($isUnread ? 600 : 400)};
`;

// Same size and position as the Odoo chat window (cl_livechat_customize).
export const Panel = styled.div`
  position: fixed;
  right: 32px;
  bottom: 32px;
  z-index: ${zIndex.floatingButton};
  display: flex;
  flex-direction: column;
  width: 334px;
  height: min(505px, calc(100vh - 64px));
  border-radius: 16px;
  background: #ffffff;
  color: #1f2937;
  font-family: ${theme.fonts.primary};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;

  @media (max-width: 767px) {
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px 12px 16px;
  border-bottom: 1px solid #e3e6ec;
`;

export const PanelAvatar = styled.div`
  position: relative;
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #eaf0ff;

  img {
    border-radius: 50%;
  }
`;

export const PanelDot = styled.span<{ $isOnline: boolean }>`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 10px;
  height: 10px;
  box-sizing: content-box;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: ${({ $isOnline }) => ($isOnline ? "#4CAF50" : "#9E9EA6")};
`;

export const PanelTitle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  strong {
    font-size: 16px;
    font-weight: 600;
  }

  span {
    font-size: 12px;
    color: #5e6c84;
  }
`;

export const PanelClose = styled.button`
  padding: 8px;
  border: none;
  background: transparent;
  color: #5e6c84;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
`;

export const PanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;

  h3 {
    margin: 24px 0 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.4;
  }

  p {
    margin: 8px 0 0;
    font-size: 14px;
    color: #5e6c84;
  }
`;

export const CocoWithTyping = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
`;

export const TypingBubble = styled.div`
  position: absolute;
  top: -8px;
  right: -64px;
  display: flex;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #eaf0ff;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${BLUE};
    opacity: 0.4;
    animation: coPulse 1.2s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.2s;
  }

  span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes coPulse {
    0%,
    80%,
    100% {
      opacity: 0.4;
    }
    40% {
      opacity: 1;
    }
  }
`;

export const RetryButton = styled.button`
  width: 100%;
  margin-top: 32px;
  padding: 16px;
  border: none;
  border-radius: 8px;
  background: ${BLUE};
  color: #ffffff;
  font-family: inherit;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
`;

// Closed (outside working hours) panel.
export const ClosedBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 16px;
  overflow-y: auto;
`;

export const Bubble = styled.div`
  max-width: 90%;
  padding: 12px 16px;
  border-radius: 12px;
  background: #f1f4fb;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line;
`;

export const ClosedFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e3e6ec;
  font-size: 16px;
  font-weight: 500;
`;

export const ClosedNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #424752;
`;

export const LeaveMessageButton = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 14px;
  border: none;
  border-radius: 8px;
  background: #e0ecff;
  color: ${BLUE};
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

export const ComposeRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

export const ComposeInput = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 8px 16px;
  border: 1px solid #c5c8d0;
  border-radius: 20px;
  font-family: inherit;
  font-size: 14px;
  line-height: 22px;
  resize: none;
  outline: none;

  &:focus {
    border-color: ${BLUE};
  }
`;

export const SendButton = styled.button`
  flex: none;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: ${BLUE};
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ComposeError = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: #b5322e;
`;
